import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { OrderCategory, OrderSubcategory, OrderProduct } from '../../types/order';
import api from '../../services/api';
import PlanCarousel, { ProductFieldConfigItem } from './PlanCarousel';
import ServiceAddressOrder from './ServiceAddressOrder';
import SupaSQAddressField from './SupaSQAddressField';

interface ProductSelectionProps {
  categories: OrderCategory[];
  selectedCategory: OrderCategory | null;
  selectedSubcategory: OrderSubcategory | null;
  products: OrderProduct[];
  selectedPlan: OrderProduct | null;
  /** Service address entered above plan selection (Google-validated, user can overwrite) */
  serviceAddress: string;
  onServiceAddressChange: (address: string) => void;
  /** Requested service activation date (yyyy-MM-dd), defaults to today */
  requestedActivationDate: string;
  onRequestedActivationDateChange: (date: string) => void;
  /** Building place_id carried over from the address-check widget deep link */
  initialBuildingKey?: string;
  onCategoryChange: (category: OrderCategory | null) => void;
  onSubcategoryChange: (subcategory: OrderSubcategory) => void;
  onPlanSelect: (plan: OrderProduct) => void;
}

function ProductSelection({
  categories,
  selectedCategory,
  selectedSubcategory,
  products,
  selectedPlan,
  serviceAddress,
  onServiceAddressChange,
  requestedActivationDate,
  onRequestedActivationDateChange,
  initialBuildingKey,
  onCategoryChange,
  onSubcategoryChange,
  onPlanSelect,
}: ProductSelectionProps) {
  const [productFieldsConfig, setProductFieldsConfig] = useState<ProductFieldConfigItem[]>([]);

  useEffect(() => {
    if (!selectedCategory?.slug) {
      setProductFieldsConfig([]);
      return;
    }
    api
      .getOrderProductFields({
        category: selectedCategory.slug,
        subcategory: selectedSubcategory?.slug,
      })
      .then((fields) =>
        setProductFieldsConfig(
          fields
            .filter((f) => f.is_visible)
            .sort((a, b) => a.display_order - b.display_order)
            .map((f) => ({
              field_key: f.field_key,
              display_order: f.display_order,
              default_display_label: f.default_display_label ?? undefined,
              label_override: f.label_override ?? undefined,
              is_visible: f.is_visible,
              field_type: f.field_type,
              field_options: Array.isArray(f.field_options) ? f.field_options : [],
            })),
        ),
      )
      .catch(() => setProductFieldsConfig([]));
  }, [selectedCategory?.slug, selectedSubcategory?.slug]);

  const handleCategoryChange = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    onCategoryChange(category || null);
  };
  
  const handleSubcategoryChange = (subcategoryId: string) => {
    if (selectedCategory && selectedCategory.subcategories) {
      const subcat = selectedCategory.subcategories.find(s => s.id === subcategoryId);
      if (subcat) {
        onSubcategoryChange(subcat);
      }
    }
  };

  const isSupaNetworks = selectedSubcategory?.slug === 'supa';

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory?.id || ''}
              label="Category"
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {selectedCategory && selectedCategory.subcategories && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={selectedSubcategory?.id || ''}
                label="Subcategory"
                onChange={(e) => handleSubcategoryChange(e.target.value)}
              >
                {selectedCategory.subcategories.map((subcat) => (
                  <MenuItem key={subcat.id} value={subcat.id}>
                    {subcat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {isSupaNetworks ? (
        <SupaSQAddressField
          value={serviceAddress}
          onChange={onServiceAddressChange}
          initialBuildingKey={initialBuildingKey}
        />
      ) : (
        <ServiceAddressOrder
          value={serviceAddress}
          onChange={onServiceAddressChange}
        />
      )}

      {selectedCategory?.slug === 'internet' && (
        <Box sx={{ mt: 3, maxWidth: 320 }}>
          <TextField
            label="Requested Activation Date"
            type="date"
            value={requestedActivationDate}
            onChange={(e) => onRequestedActivationDateChange(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            helperText="The date you'd like your new service activated"
          />
        </Box>
      )}

      {selectedSubcategory && products.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <PlanCarousel
            plans={[...products].sort(
              (a, b) =>
                Number(a.display_order ?? 0) - Number(b.display_order ?? 0) ||
                (a.name || '').localeCompare(b.name || '')
            )}
            selectedPlan={selectedPlan}
            onSelectPlan={onPlanSelect}
            subcategoryName={selectedSubcategory.name}
            productFieldsConfig={productFieldsConfig.length > 0 ? productFieldsConfig : undefined}
          />
        </Box>
      )}

      {selectedSubcategory && products.length === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No plans available for this subcategory
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ProductSelection;

